import { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { usePlatform } from '@/hooks/use-platform';
import { ArrowDown, Download, Store } from 'lucide-react';
import heroBg from '@/assets/decorative/coffee-coffeeback-hero-image-paisagem-1.jpg';

// Premium easing curves
const EASE_OUT_EXPO = [0.16, 1, 0.3, 1];

// Stagger animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.15,
            delayChildren: 0.1,
        },
    },
};

const itemVariants = {
    hidden: {
        opacity: 0,
        y: 30,
        filter: 'blur(10px)'
    },
    visible: {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        transition: {
            duration: 0.8,
            ease: EASE_OUT_EXPO,
        },
    },
};

export function HeroSection() {
    const { t } = useTranslation();
    const { platform } = usePlatform();
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    // Enhanced Scroll-based parallax effect
    const { scrollY } = useScroll();
    const bgY = useTransform(scrollY, [0, 500], [0, 200]); // Increased movement for visibility
    const bgScale = useTransform(scrollY, [0, 500], [1.05, 1.15]); // Subtle zoom in on scroll
    const textY = useTransform(scrollY, [0, 500], [0, -100]); // Text moves faster in opposite direction

    const handleDownload = () => {
        if (platform === 'ios') {
            window.open('https://apps.apple.com', '_blank');
        } else {
            window.open('https://play.google.com', '_blank');
        }
    };

    const handleSecondaryCta = () => {
        const element = document.getElementById('business-section');
        element?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleScroll = () => {
        const element = document.getElementById('how-it-works');
        element?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <section
            id="home"
            ref={ref}
            className="relative w-full min-h-[100dvh] overflow-hidden flex items-center"
        >
            {/* Full Width Background Image with Parallax & Scale */}
            <motion.div
                className="absolute inset-0 z-0"
                style={{ y: bgY, scale: bgScale }}
            >
                {/* Image */}
                <img
                    src={heroBg}
                    alt="Coffeeback experience"
                    className="w-full h-full object-cover object-[65%_center] md:object-right-center"
                />
            </motion.div>

            {/* Gradient Overlays for Text Readability */}
            {/* Desktop: Left-to-right fade to ensure text contrast */}
            <div className="absolute inset-0 z-10 bg-gradient-to-r from-[#F3EFEA] via-[#F3EFEA]/80 to-transparent pointer-events-none hidden md:block" />

            {/* Mobile: Gradient optimized to show background image while keeping text readable */}
            <div className="absolute inset-0 z-10 bg-gradient-to-b from-[#F3EFEA]/80 via-[#F3EFEA]/40 to-[#F3EFEA]/90 pointer-events-none md:hidden" />

            {/* Main Content Container */}
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-20 pt-20 pb-16 md:py-0">

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center w-full min-h-[60vh] lg:min-h-0">

                    {/* Content Column (Left Side) */}
                    <motion.div
                        className="col-span-1 lg:col-span-7 xl:col-span-6 flex flex-col items-center lg:items-start text-center lg:text-left"
                        style={{ y: textY }}
                    >
                        <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            animate={isInView ? "visible" : "hidden"}
                            className="w-full"
                        >
                            <div className="flex flex-col">
                                {/* Headline */}
                                <motion.div variants={itemVariants}>
                                    <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[5rem] xl:text-[5.5rem] font-display font-bold text-[#2D1810] leading-[1.05] tracking-tight drop-shadow-sm">
                                        {t('hero.headline')}
                                    </h1>
                                </motion.div>
                                {/* Subheadline */}
                                <motion.div variants={itemVariants} className="mt-4 md:mt-8">
                                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-[#6B5B4F] font-semibold leading-relaxed drop-shadow-sm">
                                        {t('hero.subheadline')}
                                    </p>
                                </motion.div>
                            </div>

                            {/* CTAs */}
                            <motion.div
                                variants={itemVariants}
                                className="mt-22 md:mt-14 flex flex-col sm:flex-row gap-4 justify-center md:justify-start w-full sm:w-auto"
                            >
                                {/* Primary CTA */}
                                <motion.div
                                    whileHover={{ scale: 1.03, y: -2 }}
                                    whileTap={{ scale: 0.98 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                                    className="w-full sm:w-auto"
                                >
                                    <Button
                                        size="lg"
                                        className="w-full sm:w-auto cursor-pointer h-14 px-8 text-lg rounded-full shadow-xl shadow-[#6F4E37]/20 hover:shadow-[#6F4E37]/40 transition-shadow duration-300 gap-3 bg-[#6F4E37] text-white hover:bg-[#5D4230] group relative overflow-hidden"
                                        onClick={handleDownload}
                                    >
                                        <motion.span
                                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full"
                                            animate={{ translateX: ["100%", "-100%"] }}
                                            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                                        />
                                        <Download size={22} className="relative z-10" />
                                        <span className="relative z-10">{t('hero.cta.primary')}</span>
                                    </Button>
                                </motion.div>

                                {/* Secondary CTA */}
                                <motion.div
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                                    className="w-full sm:w-auto"
                                >
                                    <Button
                                        variant="ghost"
                                        size="lg"
                                        className="w-full sm:w-auto h-14 px-8 text-lg rounded-full text-[#6F4E37] hover:bg-[#6F4E37]/10 border border-[#6F4E37]/20 hover:border-[#6F4E37]/40 transition-all duration-300 bg-white/50 backdrop-blur-sm"
                                        onClick={handleSecondaryCta}
                                    >
                                        <Store size={18} className="mr-2" />
                                        {t('hero.cta.secondary')}
                                    </Button>
                                </motion.div>
                            </motion.div>
                        </motion.div>
                    </motion.div>

                    {/* Spacer Column (Right Side) - Keeps layout balanced against background image */}
                    <div className="hidden lg:block lg:col-span-5 xl:col-span-6" />

                </div>
            </div>

            {/* Scroll Indicator */}
            <motion.div
                className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 cursor-pointer flex flex-col items-center gap-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5, duration: 0.6 }}
                onClick={handleScroll}
            >
                <div className="flex flex-col items-center gap-1 group">
                    <motion.span
                        className="text-[#6B5B4F]/80 font-medium tracking-widest uppercase text-[10px]"
                        animate={{ opacity: [0.6, 1, 0.6] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        SCROLL
                    </motion.span>
                    <motion.div
                        className="text-[#6F4E37] p-2 rounded-full border border-[#6F4E37]/20 bg-white/50 backdrop-blur-sm group-hover:bg-white/70 transition-colors shadow-sm"
                        animate={{ y: [0, 6, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <ArrowDown size={20} />
                    </motion.div>
                </div>
            </motion.div>
        </section>
    );
}
